import { useState, useEffect, useCallback, useMemo } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import ClearIcon from "@mui/icons-material/Clear";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import InputAdornment from "@mui/material/InputAdornment";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import GroupsIcon from "@mui/icons-material/Groups";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import SaveIcon from "@mui/icons-material/Save";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import TeamDialog from "../components/TeamDialog";
import DeleteConfirmDialog from "../components/DeleteConfirmDialog";
import { useTheme } from "@mui/styles";
import { Tooltip } from "@mui/material";
import { brainStyles } from "../../../theme/style";
import {
  deleteBrainTeam,
  fetchBrainAccessManagement,
  handleValidateEmail,
  updateBrainTeam,
  updateBrainUsers,
} from "../../../services/api";
import useApi from "../../../hooks/useApi";
import APP_CONSTANTS from "../../../utils/config";
import Loader from "../../../components/Loader/Loader";
import Error from "../../../components/Error/Error";
import { updateAlertInfo } from "../../../redux/slice/userSlice";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";

const ROLES = ["reviewer", "approver", "admin"];

const isValidEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());

export default function AccessManagement() {
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [editableColumns, setEditableColumns] = useState([]);
  const [deletedIds, setDeletedIds] = useState([]);
  const [teamDialogOpen, setTeamDialogOpen] = useState(false);
  const [editTeam, setEditTeam] = useState(null);
  const [deleteTeamTarget, setDeleteTeamTarget] = useState(null);
  const [newEmail, setNewEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("users");
  const [teamSearch, setTeamSearch] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const theme = useTheme();
  const classes = brainStyles(theme);
  const dispatch = useDispatch();
  const { status, errorMessage, callApi } = useApi(fetchBrainAccessManagement);

  const {
    status: brainTeamStatus,
    errorMessage: brainTeamError,
    callApi: brainTeamApi,
  } = useApi(updateBrainTeam);

  const {
    status: deleteTeamStatus,
    errorMessage: deleteTeamError,
    callApi: deleteBrainTeamApi,
  } = useApi(deleteBrainTeam);

  const {
    status: validateEmailStatus,
    errorMessage: validateEmailError,
    callApi: validateEmail,
  } = useApi(handleValidateEmail);

  const {
    status: brainUserStatus,
    errorMessage: brainUserError,
    callApi: updatebrainUserAPI,
  } = useApi(updateBrainUsers);

  const current_user_email = useSelector((state) => state.auth.metadata.email);

  const loadData = async () => {
    // setLoading(true);
    // const teams = ACCESS_DATA.teams;
    // const users = ACCESS_DATA.users;
    // const edit_col = ACCESS_DATA.editable_columns;
    // setTeams(teams);
    // setUsers(
    //   users.map((x) => ({
    //     id: x.id,
    //     email: x.email,
    //     role: x.role,
    //     teamId: x.teamId ?? null,
    //     originalRole: x.role,
    //     originalTeamId: x.teamId ?? null,
    //   }))
    // );
    // setEditableColumns(edit_col);
    // setDeletedIds([]);
    // setLoading(false);

    const response = await callApi();
    if (response.success) {
      const teams = response.data.teams;
      const users = response.data.users;
      const edit_col = response.data.editable_columns;
      setTeams(teams);
      setEditableColumns(edit_col);
      setUsers(
        users.map((x) => ({
          id: x.id,
          email: x.email,
          role: x.role,
          teamId: x.teamId ?? null,
          originalRole: x.role,
          originalTeamId: x.teamId ?? null,
        }))
      );
      setDeletedIds([]);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const teamById = useMemo(() => {
    const m = new Map();
    teams.forEach((t) => m.set(t.id, t));
    return m;
  }, [teams]);

  // ----- Team handlers -----
  const handleTeamSave = async (payload) => {
    try {
      const response = await brainTeamApi({ payload: payload });

      if (response.success) {
        const teamId = response.data.team_id;

        const updatedTeam = {
          id: teamId,
          name: payload.name,
          description: payload.description,
          columns: payload.columns,
        };

        setTeams((prev) =>
          payload.team_id == null
            ? [...prev, updatedTeam]
            : prev.map((team) => (team.id === teamId ? updatedTeam : team))
        );
        setTeamDialogOpen(false);
        setEditTeam(null);
      } else {
        dispatch(
          updateAlertInfo({
            message: "Failed to update the team!",
            open: true,
            severity: "error",
          })
        );
      }
    } catch (e) {
      // toast.error(e?.message || "Could not save team");
    }
  };

  const handleTeamDelete = async () => {
    if (!deleteTeamTarget) return;
    try {
      const team_id = deleteTeamTarget.id;
      const response = await deleteBrainTeamApi({ team_id: team_id });

      if (response.success) {
        dispatch(
          updateAlertInfo({
            message: "Team deleted successfully!",
            open: true,
            severity: "success",
          })
        );

        setTeams((prev) => prev.filter((item) => item.id != team_id));
        setDeleteTeamTarget(null);
      } else {
        dispatch(
          updateAlertInfo({
            message: "Failed to delete Team!",
            open: true,
            severity: "error",
          })
        );
      }
    } catch (e) {
      console.error("Error", e);
      dispatch(
        updateAlertInfo({
          message: "Internal Server Error",
          open: true,
          severity: "error",
        })
      );
    }
  };

  // ----- User handlers -----
  const addUserDraft = async () => {
    const email = newEmail.trim();
    if (!isValidEmail(email)) {
      dispatch(
        updateAlertInfo({
          message: "Enter a valid email",
          open: true,
          severity: "error",
        })
      );
      return;
    }
    if (users.some((u) => u.email.toLowerCase() === email.toLowerCase())) {
      dispatch(
        updateAlertInfo({
          message: "A user with this email already exists",
          open: true,
          severity: "error",
        })
      );
      return;
    }

    if (teams?.length === 0) {
      dispatch(
        updateAlertInfo({
          message: "No teams found. Please add a team first",
          open: true,
          severity: "error",
        })
      );
      return;
    }

    const response = await validateEmail({
      payload: { email: email },
    });

    if (response.success) {
      const data = response.data;
      if (data.status === "error") {
        dispatch(
          updateAlertInfo({
            message: data.message,
            open: true,
            severity: "error",
          })
        );
        return;
      }
    }

    const tempId = `new-${Date.now()}`;
    setUsers((prev) => [
      {
        id: tempId,
        isNew: true,
        email: email,
        role: "reviewer",
        teamId: teams[0]?.id,
      },
      ...prev,
    ]);
    setNewEmail("");
  };

  const updateDraft = (id, patch) => {
    setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, ...patch } : u)));
  };

  const removeDraft = (u) => {
    if (!u.isNew) setDeletedIds((prev) => [...prev, u.id]);
    setUsers((prev) => prev.filter((x) => x.id !== u.id));
  };

  const changes = useMemo(() => {
    const add_users = users
      .filter((u) => u.isNew)
      .map((u) => ({
        email: u.email,
        role: u.role,
        team: u.teamId ? teamById.get(u.teamId)?.name ?? null : null,
      }));
    const edited_users = users
      .filter(
        (u) =>
          !u.isNew &&
          (u.role !== u.originalRole || u.teamId !== u.originalTeamId)
      )
      .map((u) => ({
        user_id: u.id,
        role: u.role,
        team: u.teamId ? teamById.get(u.teamId)?.name ?? null : null,
      }));
    const deleted_users = deletedIds.map((id) => ({ user_id: id }));
    return { add_users, edited_users, deleted_users };
  }, [users, deletedIds, teamById]);

  const totalChanges =
    changes.add_users.length +
    changes.edited_users.length +
    changes.deleted_users.length;

  const handleSubmit = async () => {
    if (totalChanges === 0) return;
    setSaving(true);
    try {
      const response = await updatebrainUserAPI({ payload: changes });

      if (response.success) {
        dispatch(
          updateAlertInfo({
            message: "Access updated successfully!",
            open: true,
            severity: "success",
          })
        );
      } else {
        dispatch(
          updateAlertInfo({
            message: "Failed to update access, please try again later",
            open: true,
            severity: "error",
          })
        );
      }
      loadData();
    } catch (e) {
      // toast.error(e?.message || "Could not save changes");
    } finally {
      setSaving(false);
    }
  };

  const filteredTeams = useMemo(() => {
    const q = teamSearch.trim().toLowerCase();
    if (!q) return teams;
    return teams.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        (t.description || "").toLowerCase().includes(q) ||
        (t.columns || []).some((c) => c.toLowerCase().includes(q))
    );
  }, [teams, teamSearch]);

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const teamName = u.teamId ? teamById.get(u.teamId)?.name || "" : "";
      return (
        u.email.toLowerCase().includes(q) ||
        u.role.toLowerCase().includes(q) ||
        teamName.toLowerCase().includes(q)
      );
    });
  }, [users, userSearch, teamById]);

  const rowBg = (idx) => {
    return idx % 2 === 0
      ? theme.palette.brain.drawer.item_background
      : theme.palette.brain.table.row.background;
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        p: 3,
      }}
    >
      {/* Header Bar */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography
            variant="h1"
            sx={{ fontSize: "1.25rem", color: theme.palette.secondary.main2 }}
          >
            User Management
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.primary.text2 }}
          >
            Manage Brain access, roles, and permissions
          </Typography>
        </Box>
        {tab === "users" && (
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            <Chip
              size="small"
              label={`${changes.add_users.length} added`}
              sx={{
                fontSize: "0.7rem",
                bgcolor: theme.palette.brain.green.background,
                color: theme.palette.brain.green.text,
                border: `1px solid ${theme.palette.brain.green.border}`,
              }}
            />
            <Chip
              size="small"
              label={`${changes.edited_users.length} edited`}
              sx={{
                fontSize: "0.7rem",
                bgcolor: theme.palette.brain.yellow.background,
                color: theme.palette.brain.yellow.text,
                border: `1px solid ${theme.palette.brain.yellow.border}`,
              }}
            />
            <Chip
              size="small"
              label={`${changes.deleted_users.length} deleted`}
              sx={{
                fontSize: "0.7rem",
                bgcolor: theme.palette.brain.red.background,
                color: theme.palette.brain.red.text,
                border: `1px solid ${theme.palette.brain.red.border}`,
              }}
            />
            <Button
              size="small"
              variant="outlined"
              startIcon={<RestartAltIcon />}
              onClick={loadData}
              // disabled={totalChanges === 0 || saving}
              sx={{
                textTransform: "none",
                borderColor: theme.palette.secondary.main2,
                color: theme.palette.secondary.main2,
                "&:hover": { borderColor: theme.palette.secondary.main2 },
              }}
            >
              Reset
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSubmit}
              disabled={
                totalChanges === 0 ||
                saving ||
                status === APP_CONSTANTS.LOADING ||
                status === APP_CONSTANTS.INITIAL
              }
              sx={{
                height: "35px",
                fontSize: "0.75rem",
                fontWeight: 700,
                background: theme.palette.brain.green.text,
                "&:hover": {
                  background: theme.palette.brain.green.text,
                },
                "&.Mui-disabled": {
                  bgcolor:
                    theme.palette.brain.table.header.restrictedBackground,
                  color: theme.palette.brain.grey.text,
                },
                color: "#fff",
              }}
            >
              Submit Changes
            </Button>
          </Box>
        )}
      </Box>

      {status === APP_CONSTANTS.LOADING || status === APP_CONSTANTS.INITIAL ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
            width: "100%",
            height: "100%",
          }}
        >
          <Loader
            message={"Loading data..."}
            showLoadingImage={false}
            top={"50%"}
            circleStyle={{ color: theme.palette.secondary.main2 }}
          />
        </Box>
      ) : status === APP_CONSTANTS.SUCCESS ? (
        <>
          <Box
            sx={{
              borderBottom: `1px solid ${theme.palette.brain.mis.grey.border1}`,
            }}
          >
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              sx={{
                minHeight: 40,
                ".MuiTab-root": {
                  minHeight: 40,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                },
              }}
              TabIndicatorProps={{
                sx: {
                  background: theme.palette.secondary.main2,
                },
              }}
            >
              <Tab
                value="users"
                iconPosition="start"
                icon={<PeopleAltIcon sx={{ fontSize: 18 }} />}
                label={`Users (${users.length})`}
                style={{
                  color:
                    tab === "users"
                      ? theme.palette.secondary.main2
                      : theme.palette.grey[22],
                }}
              />
              <Tab
                value="teams"
                iconPosition="start"
                icon={<GroupsIcon sx={{ fontSize: 18 }} />}
                label={`Teams (${teams.length})`}
                style={{
                  color:
                    tab === "teams"
                      ? theme.palette.secondary.main2
                      : theme.palette.grey[22],
                }}
              />
            </Tabs>
          </Box>

          {tab === "users" && (
            <>
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <TextField
                  size="small"
                  placeholder="Search users by email, role, team…"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  InputProps={{
                    endAdornment: userSearch.trim() ? (
                      <InputAdornment position="end">
                        <Tooltip title="Clear search">
                          <IconButton
                            size="small"
                            aria-label="Clear search"
                            onClick={() => setUserSearch("")}
                            onMouseDown={(e) => e.preventDefault()}
                            edge="end"
                            sx={{
                              mr: 0.25,
                              color: theme.palette.secondary.main2,
                              opacity: 0.85,
                              "&:hover": {
                                opacity: 1,
                                backgroundColor: theme.palette.action.hover,
                              },
                            }}
                          >
                            <ClearIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ) : null,
                  }}
                  sx={{
                    width: 320,
                    minWidth: 160,
                    "& .MuiOutlinedInput-root": {
                      fontSize: "0.75rem",
                      color: theme.palette.brain.table.row.text,
                      pr: userSearch.trim() ? 0.5 : undefined,
                      backgroundColor:
                        theme.palette.brain.table.tableBackground,
                      "& fieldset": {
                        borderColor: theme.palette.brain.mis.grey.border1,
                      },
                      "&:hover fieldset": {
                        borderColor: theme.palette.secondary.main2,
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: theme.palette.secondary.main2,
                      },
                    },
                  }}
                />
                <Box sx={{ flex: 1 }} />
                <TextField
                  size="small"
                  placeholder="Add user by email…"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") addUserDraft();
                  }}
                  sx={{
                    width: 320,
                    minWidth: 160,
                    "& .MuiOutlinedInput-root": {
                      fontSize: "0.75rem",
                      color: theme.palette.brain.table.row.text,
                      pr: userSearch.trim() ? 0.5 : undefined,
                      backgroundColor:
                        theme.palette.brain.table.tableBackground,
                      "& fieldset": {
                        borderColor: theme.palette.brain.mis.grey.border1,
                      },
                      "&:hover fieldset": {
                        borderColor: theme.palette.secondary.main2,
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: theme.palette.secondary.main2,
                      },
                    },
                  }}
                />

                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={addUserDraft}
                  disabled={!isValidEmail(newEmail)}
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    background: theme.palette.secondary.main2,
                    "&:hover": {
                      background: theme.palette.secondary.main2,
                    },
                    "&.Mui-disabled": {
                      bgcolor:
                        theme.palette.brain.table.header.restrictedBackground,
                      color: theme.palette.brain.grey.text,
                    },
                    color: "#fff",
                  }}
                >
                  Add User
                </Button>
              </Box>

              <TableContainer
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflow: "auto",
                  border: `1px solid ${theme.palette.brain.mis.grey.border1}`,
                  backgroundColor: theme.palette.brain.table.tableBackground,
                  borderRadius: 2,
                  "&::-webkit-scrollbar": { height: 6, width: 6 },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: theme.palette.brain.mis.grey.background1,
                    borderRadius: 3,
                  },
                }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.tableHeader}>
                        Email
                      </TableCell>
                      <TableCell
                        className={classes.tableHeader}
                        sx={{ width: 150 }}
                      >
                        Role
                      </TableCell>
                      <TableCell
                        className={classes.tableHeader}
                        sx={{ width: 200 }}
                      >
                        Team
                      </TableCell>
                      <TableCell className={classes.tableHeader}>
                        Editable Columns
                      </TableCell>
                      <TableCell
                        className={classes.tableHeader}
                        sx={{ width: 90 }}
                        align="right"
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredUsers.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          align="center"
                          sx={{
                            py: 4,
                            backgroundColor:
                              theme.palette.brain.drawer.item_background,
                          }}
                        >
                          {userSearch
                            ? "No users match your search"
                            : "No users"}
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredUsers.map((u, idx) => {
                        const team = u.teamId ? teamById.get(u.teamId) : null;
                        const cols =
                          u.role === "reviewer" ? team?.columns ?? [] : [];
                        const edited =
                          !u.isNew &&
                          (u.role !== u.originalRole ||
                            u.teamId !== u.originalTeamId);
                        return (
                          <TableRow
                            key={u.id}
                            hover
                            sx={{
                              backgroundColor: rowBg(idx),
                              "& > td": { backgroundColor: rowBg(idx) },
                            }}
                          >
                            <TableCell
                              className={classes.baseCell}
                              sx={{
                                fontSize: "0.8125rem",
                                color: theme.palette.brain.table.row.text,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                {u.email}
                                {u.isNew && (
                                  <Chip
                                    label="NEW"
                                    size="small"
                                    sx={{
                                      height: 18,
                                      fontSize: "0.62rem",
                                      bgcolor:
                                        theme.palette.brain.green.background,
                                      color: theme.palette.brain.green.text,
                                    }}
                                  />
                                )}
                                {edited && (
                                  <Chip
                                    label="EDITED"
                                    size="small"
                                    sx={{
                                      height: 18,
                                      fontSize: "0.62rem",
                                      bgcolor:
                                        theme.palette.brain.yellow.background,
                                      color: theme.palette.brain.yellow.text,
                                    }}
                                  />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell className={classes.baseCell}>
                              <FormControl fullWidth size="small">
                                {current_user_email?.trim().toLowerCase() ===
                                u.email?.trim().toLowerCase() ? (
                                  <Typography
                                    sx={{
                                      fontSize: "0.75rem",
                                      textTransform: "capitalize",
                                      color: theme.palette.brain.table.row.text,
                                    }}
                                  >
                                    {u.role}
                                  </Typography>
                                ) : (
                                  <Select
                                    value={u.role}
                                    onChange={(e) =>
                                      updateDraft(u.id, {
                                        role: e.target.value,
                                      })
                                    }
                                    sx={{
                                      fontSize: "0.75rem",
                                      textTransform: "capitalize",
                                      backgroundColor:
                                        theme.palette.brain.table
                                          .tableBackground,
                                      color: theme.palette.brain.table.row.text,
                                      "& .MuiOutlinedInput-notchedOutline": {
                                        borderColor:
                                          theme.palette.brain.mis.grey.border1,
                                      },
                                      "&:hover .MuiOutlinedInput-notchedOutline":
                                        {
                                          borderColor:
                                            theme.palette.secondary.main2,
                                        },
                                      "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                        {
                                          border: `1px solid ${theme.palette.secondary.main2}`,
                                        },
                                      "& .MuiSelect-icon": {
                                        color:
                                          theme.palette.brain.table.row.text,
                                      },
                                    }}
                                    MenuProps={{
                                      PaperProps: {
                                        sx: {
                                          backgroundColor:
                                            theme.palette.brain.drawer
                                              .item_background,
                                          border: `1px solid ${theme.palette.brain.mis.grey.border1}`,
                                        },
                                      },
                                    }}
                                  >
                                    {ROLES.map((r) => (
                                      <MenuItem
                                        key={r}
                                        value={r}
                                        sx={{
                                          fontSize: "0.8rem",
                                          textTransform: "capitalize",
                                          color:
                                            theme.palette.brain.table.row.text,
                                        }}
                                      >
                                        {r}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                )}
                              </FormControl>
                            </TableCell>
                            <TableCell className={classes.baseCell}>
                              <FormControl fullWidth size="small">
                                <Select
                                  value={u.teamId ?? ""}
                                  displayEmpty
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    updateDraft(u.id, {
                                      teamId:
                                        v === "" || v == null
                                          ? null
                                          : Number(v),
                                    });
                                  }}
                                  sx={{
                                    fontSize: "0.75rem",
                                    backgroundColor:
                                      theme.palette.brain.table.tableBackground,
                                    color: theme.palette.brain.table.row.text,
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      borderColor:
                                        theme.palette.brain.mis.grey.border1,
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor:
                                          theme.palette.secondary.main2,
                                      },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                      {
                                        border: `1px solid ${theme.palette.secondary.main2}`,
                                      },
                                    "& .MuiSelect-icon": {
                                      color: theme.palette.brain.table.row.text,
                                    },
                                  }}
                                  MenuProps={{
                                    PaperProps: {
                                      sx: {
                                        backgroundColor:
                                          theme.palette.brain.drawer
                                            .item_background,
                                        border: `1px solid ${theme.palette.brain.mis.grey.border1}`,
                                      },
                                    },
                                  }}
                                >
                                  {teams.map((t) => (
                                    <MenuItem
                                      key={t.id}
                                      value={t.id}
                                      sx={{
                                        fontSize: "0.8rem",
                                        color:
                                          theme.palette.brain.table.row.text,
                                      }}
                                    >
                                      {t.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </TableCell>
                            <TableCell className={classes.baseCell}>
                              {u.role !== "reviewer" ? (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color:
                                      theme.palette.brain.table.header.text,
                                    fontStyle: "italic",
                                  }}
                                >
                                  Not applicable for {u.role}
                                </Typography>
                              ) : cols.length === 0 ? (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color:
                                      theme.palette.brain.table.header.text,
                                  }}
                                >
                                  {u.teamId
                                    ? "Team has no columns assigned"
                                    : "Assign a team to inherit columns"}
                                </Typography>
                              ) : (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 0.5,
                                  }}
                                >
                                  {cols.map((c) => (
                                    <Chip
                                      key={c}
                                      label={c.replace(/_/g, " ")}
                                      size="small"
                                      sx={{
                                        fontSize: "0.66rem",
                                        fontWeight: 500,
                                        bgcolor:
                                          theme.palette.brain.yellow.background,
                                        color: theme.palette.brain.yellow.text,
                                        border: `1px solid ${theme.palette.brain.yellow.border}`,
                                      }}
                                    />
                                  ))}
                                </Box>
                              )}
                            </TableCell>
                            <TableCell
                              className={classes.baseCell}
                              align="right"
                            >
                              {current_user_email?.trim().toLowerCase() !==
                                u.email?.trim().toLowerCase() && (
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => removeDraft(u)}
                                >
                                  <DeleteIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {tab === "teams" && (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <TextField
                  size="small"
                  placeholder="Search teams…"
                  value={teamSearch}
                  onChange={(e) => setTeamSearch(e.target.value)}
                  InputProps={{
                    endAdornment: userSearch.trim() ? (
                      <InputAdornment position="end">
                        <Tooltip title="Clear search">
                          <IconButton
                            size="small"
                            aria-label="Clear search"
                            onClick={() => setUserSearch("")}
                            onMouseDown={(e) => e.preventDefault()}
                            edge="end"
                            sx={{
                              mr: 0.25,
                              color: theme.palette.secondary.main2,
                              opacity: 0.85,
                              "&:hover": {
                                opacity: 1,
                                backgroundColor: theme.palette.action.hover,
                              },
                            }}
                          >
                            <ClearIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ) : null,
                  }}
                  sx={{
                    width: 320,
                    minWidth: 160,
                    "& .MuiOutlinedInput-root": {
                      fontSize: "0.75rem",
                      color: theme.palette.brain.table.row.text,
                      pr: userSearch.trim() ? 0.5 : undefined,
                      backgroundColor:
                        theme.palette.brain.table.tableBackground,
                      "& fieldset": {
                        borderColor: theme.palette.brain.mis.grey.border1,
                      },
                      "&:hover fieldset": {
                        borderColor: theme.palette.secondary.main2,
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: theme.palette.secondary.main2,
                      },
                    },
                  }}
                />
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setEditTeam(null);
                    setTeamDialogOpen(true);
                  }}
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    background: theme.palette.secondary.main2,
                    "&:hover": {
                      background: theme.palette.secondary.main2,
                    },
                    color: "#fff",
                  }}
                >
                  Add Team
                </Button>
              </Box>

              <TableContainer
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflow: "auto",
                  border: `1px solid ${theme.palette.brain.mis.grey.border1}`,
                  backgroundColor: theme.palette.brain.table.tableBackground,
                  borderRadius: 2,
                  "&::-webkit-scrollbar": { height: 6, width: 6 },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: theme.palette.brain.mis.grey.background1,
                    borderRadius: 3,
                  },
                }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        className={classes.tableHeader}
                        sx={{ width: 200 }}
                      >
                        Team Name
                      </TableCell>
                      <TableCell className={classes.tableHeader}>
                        Description
                      </TableCell>
                      <TableCell className={classes.tableHeader}>
                        Assigned Columns
                      </TableCell>
                      <TableCell
                        className={classes.tableHeader}
                        sx={{ width: 100 }}
                        align="right"
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredTeams.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          align="center"
                          sx={{ py: 4, color: "hsl(220, 9%, 46%)" }}
                        >
                          {teamSearch
                            ? "No teams match your search"
                            : "No teams yet"}
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredTeams.map((t, idx) => (
                        <TableRow
                          key={t.id}
                          hover
                          sx={{
                            backgroundColor: rowBg(idx),
                            "& > td": { backgroundColor: rowBg(idx) },
                          }}
                        >
                          <TableCell
                            sx={{
                              fontSize: "0.8125rem",
                              fontWeight: 600,
                              color: theme.palette.brain.table.row.text,
                            }}
                          >
                            {t.name}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontSize: "0.8125rem",
                              color: theme.palette.brain.table.row.text,
                            }}
                          >
                            {t.description || (
                              <span style={{ opacity: 0.5 }}>—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {t.columns.length === 0 ? (
                              <span
                                style={{
                                  color: theme.palette.brain.table.row.text,
                                  fontSize: "0.75rem",
                                }}
                              >
                                —
                              </span>
                            ) : (
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 0.5,
                                }}
                              >
                                {t.columns.map((c) => (
                                  <Chip
                                    key={c}
                                    label={c.replace(/_/g, " ")}
                                    size="small"
                                    sx={{
                                      fontSize: "0.68rem",
                                      fontWeight: 500,
                                      bgcolor:
                                        theme.palette.brain.yellow.background,
                                      color: theme.palette.brain.yellow.text,
                                      // border: `1px solid ${theme.palette.brain.yellow.border}`,
                                    }}
                                  />
                                ))}
                              </Box>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setEditTeam(t);
                                setTeamDialogOpen(true);
                              }}
                            >
                              <EditIcon
                                sx={{
                                  color: theme.palette.secondary.main2,
                                  fontSize: 18,
                                }}
                              />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setDeleteTeamTarget(t)}
                            >
                              <DeleteIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          <TeamDialog
            open={teamDialogOpen}
            onClose={() => {
              setTeamDialogOpen(false);
              setEditTeam(null);
            }}
            onSave={handleTeamSave}
            team={editTeam}
            teams={teams}
            editable_columns={editableColumns}
            theme={theme}
          />

          <DeleteConfirmDialog
            open={!!deleteTeamTarget}
            onClose={() => setDeleteTeamTarget(null)}
            onConfirm={handleTeamDelete}
            userName={deleteTeamTarget?.name || ""}
            theme={theme}
          />
        </>
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <Error message={errorMessage} />
        </Box>
      )}
    </Box>
  );
}
